import React, { useState } from 'react';
import { Server, Cloud, Users, Shield, Cpu, Building2, CheckCircle2, Mail, Phone, MapPin, ArrowRight, Check, Globe, Database, FileSpreadsheet, Key, Ticket, UserCog, Navigation, QrCode, Car, FileText, Sparkles, FolderOpen, PlayCircle, Activity, MonitorUp, Cog, Network } from 'lucide-react';
import OmniOpsFeatures from './OmniOpsFeatures';

export default function LandingPage({ onStartSimulator }: { onStartSimulator?: () => void }) {
  const [calcUsers, setCalcUsers] = useState<number>(3);

  const [quoteForm, setQuoteForm] = useState({
    name: '', company: '', email: '', phone: '', sector: '', currentSystem: '', projectDate: '', userCount: calcUsers.toString(), infrastructure: 'on-premise', notes: ''
  });
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);

  // Update quoteForm when calcUsers changes
  React.useEffect(() => {
    setQuoteForm(prev => ({ ...prev, userCount: calcUsers.toString() }));
  }, [calcUsers]);

  const calculateHardware = (userCount: number) => {
    if (userCount <= 3) {
      return { profile: 'Küçük Ofis (1-3 Kullanıcı)', cpu: '8 Çekirdek (vCPU)', ram: '16 GB RAM', disk: '160 GB NVMe', isCustom: false };
    } else if (userCount <= 10) {
      return { profile: 'Küçük İşletme (4-10 Kullanıcı)', cpu: '8 Çekirdek (vCPU)', ram: '16 GB RAM', disk: '160 GB NVMe', isCustom: false };
    } else if (userCount <= 15) {
      return { profile: 'Orta İşletme (11-15 Kullanıcı)', cpu: '12 Çekirdek (vCPU)', ram: '32 GB RAM', disk: '240 GB NVMe', isCustom: false };
    } else if (userCount <= 25) {
      return { profile: 'Büyük İşletme (16-25 Kullanıcı)', cpu: '16 Çekirdek (vCPU)', ram: '48 GB RAM', disk: '360 GB NVMe', isCustom: false };
    } else {
      return { profile: 'Kurumsal (25+ Kullanıcı)', cpu: 'Özel Boyutlandırma (İletişime Geçin)', ram: 'Özel Boyutlandırma', disk: 'Özel Boyutlandırma', isCustom: true };
    }
  };

  const specs = calculateHardware(calcUsers);

  const handleQuoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (quoteForm.name && quoteForm.email && quoteForm.company) {
      try {
        const response = await fetch('/api/quote', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(quoteForm),
        });
        if (response.ok) {
          setQuoteSubmitted(true);
          setQuoteForm({
            name: '',
            company: '',
            email: '',
            phone: '',
            userCount: '10',
            infrastructure: 'on-premise',
            sector: '',
            currentSystem: '',
            projectDate: '',
            notes: ''
          });
          setTimeout(() => {
            setQuoteSubmitted(false);
          }, 5000);
        } else {
          alert('Bir hata oluştu, lütfen daha sonra tekrar deneyin.');
        }
      } catch (error) {
        console.error(error);
        alert('Sunucuya bağlanılamadı.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-sky-200">
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-32 overflow-hidden bg-slate-950 text-white flex flex-col justify-center min-h-[90vh]">
        {/* Fast radial gradient background instead of CSS blur filters for better mobile performance */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(14,165,233,0.15)_0%,transparent_50%),radial-gradient(circle_at_top_right,rgba(99,102,241,0.15)_0%,transparent_40%),radial-gradient(circle_at_bottom_left,rgba(37,99,235,0.15)_0%,transparent_40%)] pointer-events-none"></div>
        
        <img 
          src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=800&q=50&blur=10" 
          fetchPriority="high" 
          alt="" 
          className="absolute inset-0 w-full h-full object-cover opacity-5 pointer-events-none" 
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-slate-950/80 to-slate-950 pointer-events-none"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-10 z-10">
          <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full bg-slate-900/80 border border-slate-700 shadow-xl shadow-sky-900/20 backdrop-blur-md">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-sky-500"></span>
            </span>
            <span className="text-slate-300 text-xs font-mono font-medium tracking-widest uppercase">1995'ten Bugüne BT Deneyimi</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight max-w-5xl mx-auto leading-[1.1]">
            İşletmenizin <br className="hidden md:block"/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-blue-200 to-indigo-300">
              Yeni Dijital Omurgası
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed font-light">
            Bilgi teknolojileri altyapınızı güçlendiriyor, profesyonel servis ağımız ve devrim niteliğindeki <strong className="text-slate-200 font-semibold">OmniOps® Saha İşletim Sistemi</strong> ile operasyonel sınırlarınızı ortadan kaldırıyoruz.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
            <button onClick={onStartSimulator} className="group relative bg-white hover:bg-slate-100 text-slate-900 px-8 py-4 rounded-xl font-bold transition-all shadow-xl shadow-white/10 flex items-center justify-center gap-2 overflow-hidden">
              <span className="relative z-10 flex items-center gap-2"><PlayCircle className="h-5 w-5 text-sky-600 group-hover:scale-110 transition-transform" /> Etkileşimli Turu Başlat</span>
            </button>
            <a href="#omniops" className="bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 px-8 py-4 rounded-xl font-semibold transition-colors flex items-center justify-center shadow-lg hover:shadow-slate-800/50">
              OmniOps®'u İnceleyin
            </a>
          </div>
        </div>
      </section>

      {/* Ana Sayfa Öne Çıkanlar (Bento Grid) */}
      <section className="py-24 relative z-20 bg-slate-950 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
             <span className="inline-block bg-gradient-to-r from-sky-600 to-sky-500 text-white px-6 py-2 rounded-full font-bold text-sm md:text-base uppercase tracking-widest mb-4 shadow-lg shadow-sky-500/30">Hizmet Alanlarımız</span>
             <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">Kapsamlı BT Çözümleri</h2>
             <p className="text-slate-400 mt-4 text-lg">Kurumunuzun tüm teknolojik ihtiyaçlarını tek merkezden karşılıyoruz.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-slate-900 to-slate-900/50 p-8 rounded-[2rem] border border-slate-800 transition-all hover:border-slate-700 group relative overflow-hidden flex flex-col justify-end min-h-[350px]">
              <div className="absolute top-8 right-8 w-32 h-32 bg-[radial-gradient(circle,rgba(14,165,233,0.1)_0%,transparent_70%)] rounded-full group-hover:bg-sky-500/20 transition-all"></div>
              <div className="bg-slate-800/50 w-16 h-16 flex items-center justify-center rounded-2xl mb-auto border border-slate-700 backdrop-blur-sm">
                <Shield className="h-8 w-8 text-sky-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 z-10 mt-8">Siber Güvenlik & Sızma Testleri</h3>
              <p className="text-slate-400 text-sm leading-relaxed z-10">
                Sistemlerinize yönelik profesyonel güvenlik testleri: Targeted, External/Internal Network, Blind, Double-Blind, Black Box ve White Box Penetrasyon test hizmetleri ile kurumunuzu güvenceye alın.
              </p>
            </div>
            
            <div className="bg-slate-900 p-8 rounded-[2rem] border border-slate-800 transition-all hover:border-slate-700 group flex flex-col min-h-[350px]">
              <div className="bg-slate-800/50 w-16 h-16 flex items-center justify-center rounded-2xl mb-auto border border-slate-700">
                <Cog className="h-8 w-8 text-indigo-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 z-10 mt-8">IT Destek Bakım</h3>
              <p className="text-slate-400 text-sm leading-relaxed z-10">
                Güvenlik duvarı, anti virüs ve veri kurtarma çözümleri. Otomatik yedekleme sistemleri, server kurulum ve kurumsal e-mail destek hizmetleri.
              </p>
            </div>
            
            <div className="bg-slate-900 p-8 rounded-[2rem] border border-slate-800 transition-all hover:border-slate-700 group flex flex-col min-h-[350px]">
              <div className="bg-slate-800/50 w-16 h-16 flex items-center justify-center rounded-2xl mb-auto border border-slate-700">
                <Server className="h-8 w-8 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 z-10 mt-8">Profesyonel Servis</h3>
              <p className="text-slate-400 text-sm leading-relaxed z-10">
                PC-Mac çevre birimleri temini, sunucu yapılandırması, network topoloji kurulumu, Microsoft yazılım temini ve donanım envanter yönetimi.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Kurumsal (Alternatif Bilgisayar) */}
      <section id="kurumsal" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <span className="inline-block bg-gradient-to-r from-sky-600 to-sky-500 text-white px-6 py-2 rounded-full font-bold text-sm md:text-base uppercase tracking-widest mb-4 shadow-lg shadow-sky-500/30">Kurumsal Kimliğimiz</span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                1995'ten Beri Güvenilir BT Çözüm Ortağınız.
              </h2>
              <p className="text-slate-600 leading-relaxed text-lg">
                Alternatif Bilgisayar olarak çeyrek asrı aşkın süredir kurumsal IT altyapıları, sistem entegrasyonları ve profesyonel teknik destek hizmetleri sunuyoruz. 
              </p>
              <p className="text-slate-600 leading-relaxed text-lg">
                Yılların getirdiği saha ve teknik servis tecrübemizi, bugün <strong>OmniOps® (Integrated Operations & Field Service OS)</strong> yazılımımız ile taçlandırıyoruz. Müşterilerimizin yaşadığı operasyonel zorlukları çok iyi biliyor ve bizzat sahada test edilmiş, kalıcı çözümler üretiyoruz.
              </p>
              <div className="pt-4">
                <a href="#servisler" className="inline-flex items-center gap-2 text-sky-700 font-bold hover:text-sky-800 transition-colors">
                  Tüm BT Servislerimizi İnceleyin <ArrowRight className="h-5 w-5" />
                </a>
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl min-h-[500px] bg-slate-950 border border-slate-800 p-6 sm:p-8 flex flex-col justify-between">
              {/* Futuristic subtle network backgrounds and glow effects */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,0.15),transparent_45%)]" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(99,102,241,0.1),transparent_50%)]" />
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_at_center,white,transparent_80%)]" />

              {/* Upper Header: System Status & Branding */}
              <div className="relative z-10 flex items-center justify-between border-b border-slate-800 pb-5">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                  </span>
                  <span className="text-[11px] font-mono font-semibold tracking-wider text-slate-400 uppercase">Alternatif BT Operasyon Paneli</span>
                </div>
                <div className="text-[11px] font-mono font-semibold text-sky-400 bg-sky-950/50 border border-sky-800/30 px-2.5 py-1 rounded-md">
                  EST. 1995
                </div>
              </div>

              {/* Middle Section: Milestone Timeline / Metric Grid */}
              <div className="relative z-10 my-auto py-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                
                {/* Metric 1: 31+ Yıl */}
                <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/60 backdrop-blur-sm hover:border-slate-700/60 transition-all hover:scale-[1.02] duration-300">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-sky-500/10 rounded-xl">
                      <Shield className="h-5 w-5 text-sky-400" />
                    </div>
                    <span className="text-xs font-semibold text-slate-300">Sektörel Tecrübe</span>
                  </div>
                  <div className="text-3xl font-black text-white font-mono tracking-tight">31+ Yıl</div>
                  <p className="text-[11px] text-slate-400 mt-1 leading-normal">1995'ten beri kesintisiz kurumsal altyapı, sistem entegrasyonu ve BT desteği.</p>
                </div>

                {/* Metric 2: Sunucu & Donanım */}
                <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/60 backdrop-blur-sm hover:border-slate-700/60 transition-all hover:scale-[1.02] duration-300">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-emerald-500/10 rounded-xl">
                      <Server className="h-5 w-5 text-emerald-400" />
                    </div>
                    <span className="text-xs font-semibold text-slate-300">Donanım & Network</span>
                  </div>
                  <div className="text-3xl font-black text-white font-mono tracking-tight">10K+</div>
                  <p className="text-[11px] text-slate-400 mt-1 leading-normal">Aktif olarak yönetilen kurumsal donanım envanteri ve geniş alan ağ ulaştırma topolojisi.</p>
                </div>

                {/* Metric 3: OmniOps® Entegrasyonu */}
                <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/60 backdrop-blur-sm hover:border-slate-700/60 transition-all hover:scale-[1.02] duration-300 col-span-1 sm:col-span-2">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-500/10 rounded-xl">
                        <Activity className="h-5 w-5 text-indigo-400" />
                      </div>
                      <span className="text-xs font-semibold text-slate-300">OmniOps® Entegre İşletim Sistemi</span>
                    </div>
                    <span className="text-[10px] font-mono font-medium text-emerald-400 bg-emerald-950/40 border border-emerald-800/30 px-2 py-0.5 rounded self-start sm:self-auto">Aktif Koruma</span>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    Saha operasyonları, otonom izleme sistemleri, taşeron kilit mekanizmaları ve finansal izolasyon filtrelerini tek bir kapalı devre platformda birleştiren devrim niteliğinde yazılım altyapısı.
                  </p>
                </div>

              </div>

              {/* Lower Footer: Technical Specs & Visual Accent */}
              <div className="relative z-10 border-t border-slate-800 pt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Cpu className="w-4 h-4 text-slate-500" />
                  <span>Güvenli Donanım & Sistem Entegratörü</span>
                </div>
                <div className="flex items-center gap-1.5 bg-slate-900 px-3 py-1 rounded-full border border-slate-800">
                  <Sparkles className="w-3.5 h-3.5 text-sky-400" />
                  <span className="text-[10px] font-mono font-semibold text-slate-300 uppercase tracking-wider">Kesintisiz Güvence</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Servislerimiz */}
      <section id="servisler" className="py-24 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="inline-block bg-gradient-to-r from-sky-600 to-sky-500 text-white px-6 py-2 rounded-full font-bold text-sm md:text-base uppercase tracking-widest mb-4 shadow-lg shadow-sky-500/30">Hizmetlerimiz</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Servis & Teknoloji Çözümlerimiz
            </h2>
            <p className="text-slate-600 text-lg">
              İşletmenizin tüm bilgi işlem alt yapısının kurulumu, sorunsuz olarak çalışması ve düzenlenmesini sağlamak amacıyla uzmanlarımızla destek sağlıyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-3xl border border-slate-200">
              <div className="bg-slate-100 w-12 h-12 flex items-center justify-center rounded-xl text-slate-700 mb-6">
                <Shield className="h-6 w-6 text-sky-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-3">Ağ Güvenliği</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Firewalls, Network access control (NAC), Intrusyon detection and prevention systems (IDPSs), Virtual private networks (VPNs), Application security, Email security.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-slate-200">
              <div className="bg-slate-100 w-12 h-12 flex items-center justify-center rounded-xl text-slate-700 mb-6">
                <Cpu className="h-6 w-6 text-sky-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-3">Donanım Desteği</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Bilgisayar sisteminin kurulması, gerekli işletim sistemlerinin ve donanımsal parçaların tedarik edilmesi, yükseltilmesi konusunda son kullanıcı desteği.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-slate-200">
              <div className="bg-slate-100 w-12 h-12 flex items-center justify-center rounded-xl text-slate-700 mb-6">
                <Server className="h-6 w-6 text-sky-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-3">Sistem Desteği</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Sistem incelenmesi, yükseltmeler, lisanslama işlemleri. Düzenli manuel veya otomatik yedekleme ve veri kurtarma çözümleri.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-slate-200">
              <div className="bg-slate-100 w-12 h-12 flex items-center justify-center rounded-xl text-slate-700 mb-6">
                <Cloud className="h-6 w-6 text-sky-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-3">Network Sistemleri</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Network yapısının projelendirilmesi, kablo montajı, cihaz kurulumları. Windows Server kurulumları ve Network hizmetlerinin devreye alınması.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-slate-200">
              <div className="bg-slate-100 w-12 h-12 flex items-center justify-center rounded-xl text-slate-700 mb-6">
                <Building2 className="h-6 w-6 text-sky-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-3">Yazılım Desteği</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Office 365, Office 2019, Windows ve Linux işletim sistemleri kurulum ve desteği. Anti-Virus (Kaspersky) ve üçüncü parti program desteği.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-slate-200">
              <div className="bg-slate-100 w-12 h-12 flex items-center justify-center rounded-xl text-slate-700 mb-6">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-3">Sarf Malzemesi Temini</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Tüm marka PC modelleri, OEM malzeme, veri depolama ürünleri, ağ ürünleri, bilgisayar konfigürasyonu ve NAS depolama üniteleri temini.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OmniOps Nedir */}
      {/* OmniOps Nedir */}
      <section id="omniops" className="py-32 bg-[#f8fafc] border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-12">
          <div className="space-y-4 max-w-4xl mx-auto">
            <span className="inline-block bg-gradient-to-r from-sky-600 to-sky-500 text-white px-6 py-2 rounded-full font-bold text-base uppercase tracking-widest mb-4 shadow-lg shadow-sky-500/30">
              Özel Ürünümüz
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              OmniOps® (Integrated Operations & Field Service OS) ile Tanışın
            </h2>
            <div className="text-slate-600 text-lg space-y-4 text-center mt-6">
              <p className="font-semibold text-slate-800 text-xl md:text-2xl">Operasyonlarınızın Yeni Dijital Omurgası</p>
              <p className="leading-relaxed">
                OmniOps®, klasik iş takip yazılımlarının ötesine geçerek; ağ izleme, uzaktan bağlantı araçları, canlı saha haritalandırması (Dispatch), alt-yüklenici (taşeron) yönetimi ve finansal izolasyonu tek bir kapalı devre sistemde birleştiren yeni nesil bir İşletim Sistemidir.
              </p>
              <p className="leading-relaxed">
                Bir arızanın oluşmasından, teknisyenin sahadaki ıslak imzasına ve nihai faturanın kesilmesine kadar geçen tüm yaşam döngüsünü sıfır veri kaçağı ve maksimum ticari mahremiyet ile yönetir. OmniOps®, reaktif (arıza olunca haber alan) değil, proaktif (arızayı otonom tespit eden) bir mimariye sahiptir.
              </p>
            </div>
          </div>
          <OmniOpsFeatures />
          <div className="pt-12">
            <button onClick={onStartSimulator} className="mx-auto bg-[#E30613] hover:bg-red-700 text-white px-10 py-5 rounded-2xl text-lg font-bold transition-all shadow-xl shadow-red-500/20 flex items-center gap-3 hover:-translate-y-1">
              <PlayCircle className="h-6 w-6" /> 
              OmniOps® İnteraktif Simülatörü Başlat
            </button>
          </div>
        </div>
      </section>

      {/* Altyapı ve Sistem Gereksinimleri */}
      <section id="altyapi" className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[radial-gradient(circle_at_center,rgba(14,165,233,0.1)_0%,transparent_70%)] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16 relative">
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <span className="inline-block bg-gradient-to-r from-sky-600 to-sky-500 text-white px-6 py-2 rounded-full font-bold text-sm md:text-base uppercase tracking-widest mb-4 shadow-lg shadow-sky-500/30">Teknik Altyapı</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              Kurulum Şartları ve Ön Koşullar
            </h2>
            <p className="text-slate-300">
              OmniOps®, güvenliğiniz ve veri mahremiyetiniz için "Self-Hosted" mimariyle çalışır. Kurulum için ihtiyacınız olan sunucu altyapısını işletmenizin IT kapasitesine göre iki farklı şekilde planlayabiliriz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* On-Premise */}
            <div className="bg-slate-800/50 p-8 rounded-3xl border border-slate-700 backdrop-blur-sm">
              <div className="flex items-center gap-4 mb-6">
                <Server className="h-8 w-8 text-sky-400" />
                <h3 className="text-2xl font-bold">Kendi Sunucunuz (On-Premise)</h3>
              </div>
              <p className="text-slate-300 text-sm mb-6 leading-relaxed">
                Mevcut şirket içi veri merkezinizdeki sunucular (VMware, Hyper-V vb.) üzerinde çalışan, tamamen kurum ağınıza entegre bir yapıdır. Tüm donanım, yedekleme (backup) ve ağ güvenliği sorumluluğu şirketinize aittir.
              </p>
              <div className="bg-slate-900 rounded-xl p-6 border border-slate-800 space-y-5">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="user-slider" className="text-xs font-mono text-sky-400">Aktif Kullanıcı Sayısı (Siz + Taşeronlar)</label>
                    <span className="text-white font-bold bg-slate-800 px-3 py-1 rounded-lg text-sm">{calcUsers > 25 ? '25+ Kişi' : `${calcUsers} Kişi`}</span>
                  </div>
                  <input 
                    id="user-slider"
                    type="range" 
                    min="1" 
                    max="26" 
                    value={calcUsers}
                    onChange={(e) => setCalcUsers(parseInt(e.target.value))}
                    className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-sky-500"
                  />
                  <div className="flex justify-between text-[10px] text-slate-400 mt-2 font-mono">
                    <span>1</span>
                    <span>25+</span>
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t border-slate-800">
                  <div className="flex justify-between items-center text-sm border-b border-slate-800/50 pb-2">
                    <span className="text-slate-300">Önerilen Performans Paketi</span>
                    <span className="font-semibold text-sky-400">{specs.profile}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-b border-slate-800/50 pb-2">
                    <span className="text-slate-300">İşletim Sistemi</span>
                    <span className="font-semibold text-white">Ubuntu 22.04 LTS (Linux)</span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-b border-slate-800/50 pb-2">
                    <span className="text-slate-300">İşlemci (CPU)</span>
                    <span className={`font-semibold transition-all ${specs.isCustom ? 'text-sky-400' : 'text-white'}`}>{specs.cpu}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-b border-slate-800/50 pb-2">
                    <span className="text-slate-300">Bellek (RAM)</span>
                    <span className={`font-semibold transition-all ${specs.isCustom ? 'text-sky-400' : 'text-white'}`}>{specs.ram}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-300">Depolama</span>
                    <span className={`font-semibold transition-all ${specs.isCustom ? 'text-sky-400' : 'text-white'}`}>{specs.disk}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Alternatif Cloud */}
            <div className="bg-sky-900/20 p-8 rounded-3xl border border-sky-500/30 backdrop-blur-sm">
              <div className="flex items-center gap-4 mb-6">
                <Cloud className="h-8 w-8 text-sky-400" />
                <h3 className="text-2xl font-bold">Alternatif Bulut (Managed Cloud)</h3>
              </div>
              <p className="text-sky-100/80 text-sm mb-6 leading-relaxed">
                Herhangi bir sunucu donanımınız yoksa veya IT işletimiyle uğraşmak istemiyorsanız, altyapıyı sizin adınıza biz sağlarız. Sunucu donanımı, 7/24 kesintisiz erişim, siber güvenlik (WAF) ve veri yedekleme Alternatif Bilgisayar sorumluluğundadır.
              </p>
              <div className="bg-sky-950/50 rounded-xl p-6 border border-sky-500/20 space-y-6 mt-8">
                <div className="text-xs font-mono text-sky-400 uppercase tracking-wider">Hizmet Kapsamı ve Avantajlar</div>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="bg-sky-900/50 p-2 rounded-lg">
                      <Shield className="h-5 w-5 text-sky-400" />
                    </div>
                    <div className="text-sm">
                      <div className="text-white font-semibold">Uçtan Uca Güvenlik (WAF)</div>
                      <div className="text-sky-100/70 mt-1 leading-relaxed">Güvenlik duvarı yönetimi ve düzenli sistem güncellemeleri tarafımızca yapılır.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-sky-900/50 p-2 rounded-lg">
                      <CheckCircle2 className="h-5 w-5 text-sky-400" />
                    </div>
                    <div className="text-sm">
                      <div className="text-white font-semibold">İş Sürekliliği & Felaket Kurtarma</div>
                      <div className="text-sky-100/70 mt-1 leading-relaxed">Verileriniz günlük olarak yedeklenir, olası felaket senaryolarına karşı hazırlıklıdır.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-sky-900/50 p-2 rounded-lg">
                      <Cpu className="h-5 w-5 text-sky-400" />
                    </div>
                    <div className="text-sm">
                      <div className="text-white font-semibold">Sıfır Donanım Yatırımı (Capex)</div>
                      <div className="text-sky-100/70 mt-1 leading-relaxed">Fiziksel sunucu maliyeti veya ekstra IT personeli gerektirmez.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800/80 p-6 rounded-2xl border border-slate-700 flex items-start gap-4">
            <Cpu className="h-6 w-6 text-sky-400 shrink-0 mt-1" />
            <p className="text-sm text-slate-300 leading-relaxed">
              <strong className="text-white block mb-1">Fiyatlandırma Neye Göre Belirleniyor?</strong> 
              OmniOps®'ta kullanıcı başı lisans ücreti <span className="text-sky-400 underline underline-offset-2">yoktur</span>. Ancak sistemdeki eşzamanlı aktif (online) personel ve taşeron sayınız, veritabanı üzerindeki işlem yükünü belirler. Fiyat teklifi aşamasında belirteceğiniz aktif kullanıcı sayısı baremi, size sağlanacak donanım kapasitesini (CPU, RAM, Disk) şekillendireceği için başlangıç paketinizin fiyatını belirler.
            </p>
          </div>
        </div>
      </section>

      {/* Onboarding Data */}
      <section id="onboarding" className="py-24 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="inline-block bg-gradient-to-r from-sky-600 to-sky-500 text-white px-6 py-2 rounded-full font-bold text-sm md:text-base uppercase tracking-widest mb-4 shadow-lg shadow-sky-500/30">Kurulum & Onboarding</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Projeye Başlarken Sizden İstediklerimiz
            </h2>
            <p className="text-slate-600 text-lg">
              Satın alma sonrası kurulum sürecinin hızlı ve sorunsuz ilerlemesi için, sisteminize özel olarak hazırlanacak altyapı ve yetkilendirme bilgilerine ihtiyaç duyuyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* 1. Kurumsal Kimlik */}
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-sky-50 p-3 rounded-2xl">
                  <Building2 className="h-6 w-6 text-sky-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">1. Kurumsal Kimlik & İletişim</h3>
              </div>
              <p className="text-slate-500 text-sm mb-6">Sistemin fatura, proforma üretmesi ve müşterilere kurumsal e-posta atabilmesi için gereklidir.</p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-slate-900 block">Fatura & Şirket Bilgileri</strong>
                    <span className="text-slate-600 mt-1 block">Şirketin tam resmi unvanı, vergi dairesi, vergi numarası ve resmi adresi (Proforma PDF'lerine basılacak).</span>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-slate-900 block">Marka Varlıkları</strong>
                    <span className="text-slate-600 mt-1 block">Yüksek çözünürlüklü logolarınız (Açık ve koyu tema için) ve kurumsal renk kodlarınız (HEX).</span>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-slate-900 block">SMTP (E-Posta) Yetkileri</strong>
                    <span className="text-slate-600 mt-1 block">Sistemin sizin adınıza mail atabilmesi için gönderim yapacak e-posta adresi (örn: destek@), SMTP uygulama şifresi ve port bilgileri.</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* 2. Ağ ve Alan Adı */}
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-sky-50 p-3 rounded-2xl">
                  <Globe className="h-6 w-6 text-sky-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">2. Ağ & Alan Adı Yönlendirmeleri</h3>
              </div>
              <p className="text-slate-500 text-sm mb-6">Sistemin dış dünyaya nasıl açılacağını, yetkili erişim noktalarını ve güvenlik duvarı kurallarını belirler.</p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-slate-900 block">Alt Alan Adları (Subdomains)</strong>
                    <span className="text-slate-600 mt-1 block">Sistemin arayüzleri için kullanmak istedikleri adresler (Örn: portal.firma.com, servis.firma.com).</span>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-slate-900 block">Statik IP (On-Premise İçin)</strong>
                    <span className="text-slate-600 mt-1 block">Müşteri kendi sunucusunu kullanacaksa, sunucunun dışarıya açık statik IP adresi ve şirketin dış bacak IP'si (Güvenlik duvarı beyaz listesi için).</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* 3. Kullanıcı Hiyerarşisi */}
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-sky-50 p-3 rounded-2xl">
                  <Users className="h-6 w-6 text-sky-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">3. Yetkilendirme (RBAC)</h3>
              </div>
              <p className="text-slate-500 text-sm mb-6">Sistemi boş teslim etmiyoruz; ilk yöneticiler ve departmanların kurulum sırasında tanımlanması gerekir.</p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-slate-900 block">Süper Yöneticiler</strong>
                    <span className="text-slate-600 mt-1 block">Sistemin tam yetkili yöneticisi olacak kişilerin ad-soyad, telefon ve e-posta bilgileri.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-slate-900 block">Personel / Departman Listesi</strong>
                    <span className="text-slate-600 mt-1 block">Sisteme giriş yapacak iç personellerin listesi (Finans, Operasyon, Saha Teknisyenleri).</span>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-slate-900 block">Taşeron Listesi</strong>
                    <span className="text-slate-600 mt-1 block">Eğer sistemde dışarıdan iş verilen alt yükleniciler olacaksa, bu firmaların isimleri ve iletişim e-postaları.</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* 4. Başlangıç Verileri */}
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-sky-50 p-3 rounded-2xl">
                  <Database className="h-6 w-6 text-sky-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">4. Başlangıç Verileri (Seed Data)</h3>
              </div>
              <p className="text-slate-500 text-sm mb-6">Müşteri sistemi açtığında operasyonlarına hemen başlayabilmesi için gerekli başlangıç veritabanı aktarımı.</p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-slate-900 block">Müşteri / Cari Havuzu</strong>
                    <span className="text-slate-600 mt-1 block">Hizmet verdikleri mevcut müşterilerin Excel veya CSV formatında listesi (İlk aktarım / migration için).</span>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-slate-900 block">Hizmet / Branş Kalemleri</strong>
                    <span className="text-slate-600 mt-1 block">Hangi konularda teknik servis veya hizmet verildiği (Örn: IT Servisleri, İklimlendirme, Elektrik).</span>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-slate-900 block">Fiyatlandırma Mantığı</strong>
                    <span className="text-slate-600 mt-1 block">Proforma üretilirken sistemin otomatik ekleyeceği standart KDV Oranı ve varsa standart Kâr Marjı yüzdesi.</span>
                  </div>
                </li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* Teklif Formu */}
      <section id="teklif" className="py-24 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 p-8 md:p-12 rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/50">
            <div className="mb-10 text-center">
              <span className="inline-block bg-gradient-to-r from-sky-600 to-sky-500 text-white px-6 py-2 rounded-full font-bold text-sm md:text-base uppercase tracking-widest mb-4 shadow-lg shadow-sky-500/30">Teklif İsteyin</span>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mt-2">OmniOps® (Integrated Operations & Field Service OS) Fiyatlandırma ve Özellik Kataloğu</h3>
              <p className="text-slate-500 text-sm md:text-base mt-4 max-w-2xl mx-auto">
                İşletmenizin altyapı gereksinimlerine en uygun OmniOps® donanım paketi fiyatlarını ve detaylı özellik listesini e-posta adresinize gönderelim. Müşteri bilgileriniz ve kapasite planlama detayları teklif oluşturmak için gereklidir.
              </p>
            </div>

            {quoteSubmitted ? (
              <div className="bg-sky-50 text-sky-800 p-8 rounded-2xl text-center space-y-4 border border-sky-100">
                <CheckCircle2 className="h-12 w-12 mx-auto text-green-500" />
                <h3 className="text-xl font-bold">Talebiniz Bize Ulaştı</h3>
                <p className="text-sm">Belirttiğiniz e-posta adresine OmniOps® detaylı özellikleri ve tahmini altyapı fiyatlandırma kataloğu en kısa sürede iletilecektir.</p>
              </div>
            ) : (
              <form onSubmit={handleQuoteSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="quote-name" className="text-xs font-semibold text-slate-700">Adınız Soyadınız</label>
                    <input id="quote-name" type="text" required value={quoteForm.name} onChange={e => setQuoteForm({...quoteForm, name: e.target.value})} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all" />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="quote-company" className="text-xs font-semibold text-slate-700">Şirket Adı</label>
                    <input id="quote-company" type="text" required value={quoteForm.company} onChange={e => setQuoteForm({...quoteForm, company: e.target.value})} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all" />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="quote-email" className="text-xs font-semibold text-slate-700">E-posta Adresi</label>
                    <input id="quote-email" type="email" required value={quoteForm.email} onChange={e => setQuoteForm({...quoteForm, email: e.target.value})} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all" />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="quote-phone" className="text-xs font-semibold text-slate-700">Telefon Numaranız</label>
                    <input id="quote-phone" type="tel" value={quoteForm.phone} onChange={e => setQuoteForm({...quoteForm, phone: e.target.value})} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="quote-sector" className="text-xs font-semibold text-slate-700">Firma Sektörü</label>
                    <input id="quote-sector" type="text" value={quoteForm.sector} onChange={e => setQuoteForm({...quoteForm, sector: e.target.value})} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all" placeholder="Örn: Telekom, Enerji, Güvenlik" />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="quote-current-system" className="text-xs font-semibold text-slate-700">Mevcut Yazılım Altyapısı</label>
                    <input id="quote-current-system" type="text" value={quoteForm.currentSystem} onChange={e => setQuoteForm({...quoteForm, currentSystem: e.target.value})} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all" placeholder="Şu an kullandığınız bir program var mı?" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="quote-project-date" className="text-xs font-semibold text-slate-700">Hedeflenen Proje Başlangıcı</label>
                  <select id="quote-project-date" value={quoteForm.projectDate} onChange={e => setQuoteForm({...quoteForm, projectDate: e.target.value})} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all">
                    <option value="">Seçiniz</option>
                    <option value="hemen">Hemen / 1 Ay İçinde</option>
                    <option value="1-3_ay">1-3 Ay İçinde</option>
                    <option value="3-6_ay">3-6 Ay İçinde</option>
                    <option value="6+_ay">6 Aydan Sonra / Sadece Araştırma Aşamasındayız</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="quote-user-count" className="text-xs font-semibold text-slate-700">Aktif Kullanıcı / Personel Sayısı (Donanım Planlaması İçin)</label>
                  <input 
                    id="quote-user-count"
                    type="number" 
                    min="1"
                    required 
                    value={quoteForm.userCount} 
                    onChange={e => {
                      setQuoteForm({...quoteForm, userCount: e.target.value});
                      const parsed = parseInt(e.target.value);
                      if (!isNaN(parsed) && parsed > 0) setCalcUsers(parsed);
                    }} 
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all" 
                  />
                  <p className="text-xs text-slate-500 mt-1">Sistemi aynı anda kullanacak tahmini personel sayısını belirtmeniz sunucu maliyetleri ve teklifin doğruluğu için önemlidir. Yukarıdaki donanım hesaplayıcı ile entegredir.</p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-700">Tercih Edilen Altyapı (Zorunlu)</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className={`border rounded-xl p-4 cursor-pointer transition-all flex items-start gap-3 ${quoteForm.infrastructure === 'on-premise' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                      <input type="radio" name="infra" value="on-premise" checked={quoteForm.infrastructure === 'on-premise'} onChange={() => setQuoteForm({...quoteForm, infrastructure: 'on-premise'})} className="mt-1 text-sky-600 focus:ring-sky-500" />
                      <div>
                        <div className="font-semibold text-sm text-slate-900">Kendi Sunucumuz</div>
                        <div className="text-xs text-slate-600 mt-1">Donanım bize ait (On-Premise)</div>
                      </div>
                    </label>
                    <label className={`border rounded-xl p-4 cursor-pointer transition-all flex items-start gap-3 ${quoteForm.infrastructure === 'cloud' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                      <input type="radio" name="infra" value="cloud" checked={quoteForm.infrastructure === 'cloud'} onChange={() => setQuoteForm({...quoteForm, infrastructure: 'cloud'})} className="mt-1 text-sky-600 focus:ring-sky-500" />
                      <div>
                        <div className="font-semibold text-sm text-slate-900">Alternatif Bulut</div>
                        <div className="text-xs text-slate-600 mt-1">Siz yönetin (Managed Cloud)</div>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-700">Ek Notlar (Opsiyonel)</label>
                  <textarea rows={3} value={quoteForm.notes} onChange={e => setQuoteForm({...quoteForm, notes: e.target.value})} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all resize-none" placeholder="Şirketinizin özel bir durumu veya sormak istediğiniz öncelikli bir konu varsa buraya yazabilirsiniz."></textarea>
                </div>

                <button type="submit" className="w-full bg-[#E30613] hover:bg-red-700 text-white font-bold py-4 rounded-xl transition-colors shadow-lg shadow-red-500/20">
                  Talebi Gönder
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* İletişim */}
      <section id="iletisim" className="py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 bg-gradient-to-r from-sky-600 to-sky-500 text-white px-6 py-2 rounded-full font-bold text-sm md:text-base uppercase tracking-widest mb-6 shadow-lg shadow-sky-500/30">
            <Mail className="h-5 w-5" /> Bize Ulaşın
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4">
            İletişim & Destek
          </h2>
          <p className="text-lg text-slate-600 mb-12 max-w-2xl mx-auto">
            Ağ güvenliği, donanım desteği, sistem desteği, network sistemleri, yazılım desteği, OmniOps® fiyatlandırması ve aklınıza takılan tüm sorular için bize ulaşabilirsiniz.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/20 flex flex-col items-center text-center">
              <div className="bg-sky-50 p-4 rounded-2xl mb-6 text-sky-600">
                <Mail className="h-8 w-8" />
              </div>
              <h3 className="font-bold text-slate-900 text-lg mb-2">E-Posta Adreslerimiz</h3>
              <div className="flex flex-col space-y-3">
                <div>
                  <p className="text-slate-500 text-sm mb-1">Genel İletişim:</p>
                  <a href="mailto:alternatif.omniops@gmail.com" className="text-sky-700 font-semibold hover:text-sky-800 transition-colors">alternatif.omniops@gmail.com</a>
                </div>
                <div>
                  <p className="text-slate-500 text-sm mb-1">OmniOps® Bilgi:</p>
                  <a href="mailto:omniops.fs.os@gmail.com" className="text-sky-700 font-semibold hover:text-sky-800 transition-colors">omniops.fs.os@gmail.com</a>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/20 flex flex-col items-center text-center">
              <div className="bg-sky-50 p-4 rounded-2xl mb-6 text-sky-600">
                <Phone className="h-8 w-8" />
              </div>
              <h3 className="font-bold text-slate-900 text-lg mb-2">Telefon Numaralarımız</h3>
              <p className="text-slate-600 mb-2">Bilgisayar Bölümü:</p>
              <a href="tel:+905493598596" className="text-sky-700 font-semibold block mb-1 hover:text-sky-800 transition-colors">+90 549 359 85 96</a>
              <a href="tel:+905493858487" className="text-sky-700 font-semibold hover:text-sky-800 transition-colors">+90 549 385 84 87</a>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/20 flex flex-col items-center text-center">
              <div className="bg-sky-50 p-4 rounded-2xl mb-6 text-sky-600">
                <MapPin className="h-8 w-8" />
              </div>
              <h3 className="font-bold text-slate-900 text-lg mb-2">Merkez Ofisimiz</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Dere Mahallesi<br/>
                Selanik Sokak Kardelen Apt.<br/>
                No:5/5 Merkez / YALOVA
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
